//@ts-nocheck

import React, { ReactNode, forwardRef } from 'react';
import { Input as AntdInput } from 'antd';
import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import cn from 'classnames';
import { twMerge } from 'tailwind-merge';
import HelperText from './HelperText';
import Label from './Label';

type Props = {
  id?: string;
  value?: string | number | readonly string[];
  statusIcon?: boolean;
  CustomIcon?: ReactNode;
  status?: 'error' | 'success';
  disabled?: boolean;
  helperText?: string;
  label?: React.ReactNode;
  required?: boolean;
  optional?: boolean;
  autoComplete?: string;
  labelHelperText?: string;
  labelClassName?: string;
  classNameContainer?: string;
} & Omit<React.ComponentProps<typeof AntdInput>, 'status'>;

export const inputBaseClasses =
  '!h-[50px] !rounded-[12px] !border !border-[#E7E7E9] hover:border-pink-500 focus-within:border-pink-500 dark:border-dark-input-border focus-within:shadow-none focus-within:border-2 transition-all bg-white dark:!bg-[#000000] focus-within:bg-white dark:focus-within:!bg-[#000000] !py-[12px] !pl-[20px] !pr-[16px] !gap-[10px] dark:!text-white dark:[&_input]:!text-white [&_input::placeholder]:!text-[#ACACAC] dark:[&_input::placeholder]:!text-dark-text-placeholder [&::placeholder]:!text-[#ACACAC] dark:[&.ant-input-affix-wrapper]:!bg-[#000000] dark:[&.ant-input-affix-wrapper-focused]:!bg-[#000000] text-sm';

const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      id,
      value,
      disabled,
      status,
      statusIcon,
      CustomIcon,
      helperText,
      label,
      labelId,
      required,
      optional,
      autoComplete,
      labelHelperText,
      labelClassName,
      classNameContainer,
      className,
      ...props
    },
    ref
  ) => {
    const statusIcons = React.useMemo(
      () => ({
        error: (
          <InfoCircleOutlined
            className={cn('text-red-500', {
              'text-light-text-secondary dark:text-dark-text-secondary':
                disabled,
            })}
          />
        ),
        success: (
          <CheckCircleOutlined
            className={cn('text-green-500', {
              'text-light-text-secondary dark:text-dark-text-secondary':
                disabled,
            })}
          />
        ),
      }),
      [disabled]
    );

    return (
      <div className={classNameContainer}>
        {label && (
          <Label
            labelHelperText={labelHelperText}
            id={labelId}
            required={required}
            optional={optional}
            htmlFor={id}
            text={label}
            className={labelClassName}
          />
        )}
        <AntdInput
          autoComplete={autoComplete}
          value={value}
          id={id}
          disabled={disabled}
          suffix={statusIcon && status && statusIcons[status]}
          prefix={CustomIcon}
          {...props}
          ref={ref}
          className={twMerge(
            cn(`${inputBaseClasses} ${className}`, {
              '!border-red-500  ': status === 'error',
              '!border-green-500 ': status === 'success',
              'border-0 dark:border-0 !bg-light-button-label-primary dark:!bg-dark-button-label-primary':
                disabled,
              '[&_input]:!border-none [&_input]:!shadow-none [&_input]:!bg-transparent [&_input]:focus:!border-none [&_input]:focus:!shadow-none':
                !!props.addonBefore,
            })
          )}
        />
        {helperText && (
          <HelperText text={helperText} status={status} disabled={disabled} />
        )}
      </div>
    );
  }
);

Input.displayName = 'Input'; // Recommended for debugging with forwardRef

export default Input;
